import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Building2, Clock, Tag } from "lucide-react";
import { getCaseStudy, getNextCaseStudy } from "../data/caseStudies";
import { fadeUp, stagger } from "../lib/animations";

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <div className="text-xs uppercase tracking-widest text-brand-ink/40">{label}</div>
      <div className="mt-0.5 font-semibold text-brand-ink">{value}</div>
    </div>
  </div>
);

const Chapter = ({ num, title, children }) => (
  <div className="relative pt-10">
    <span className="pointer-events-none select-none absolute -top-4 -left-1 font-heading font-extrabold text-[6rem] leading-none text-brand/[0.06]">
      {num}
    </span>
    <div className="relative">
      <div className="h-px w-12 bg-brand mb-5" />
      <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-brand-ink">
        {title}
      </h2>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

export const CaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = getCaseStudy(slug);

  if (!cs) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="case-study-not-found">
        <h1 className="font-heading font-extrabold text-4xl text-brand-ink">Project not found</h1>
        <p className="mt-4 text-brand-ink/60">The case study you're looking for doesn't exist.</p>
        <Link to="/#work" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-white hover:bg-brand transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to work
        </Link>
      </main>
    );
  }

  const next = getNextCaseStudy(slug);

  return (
    <main className="bg-white pt-28 sm:pt-32" data-testid="case-study-page">
      {/* Header */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <Link
          to="/#work"
          data-testid="case-study-back"
          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/60 hover:text-brand transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> All work
        </Link>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="mt-8 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <motion.span variants={fadeUp} className="text-xs uppercase tracking-[0.22em] font-semibold text-brand">
              {cs.category}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-4 font-heading font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl text-brand-ink"
            >
              {cs.title}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 max-w-2xl text-lg sm:text-xl leading-relaxed text-brand-ink/60">
              {cs.summary}
            </motion.p>
          </div>
          <motion.div variants={fadeUp} className="col-span-12 lg:col-span-4 grid gap-5 lg:pt-10">
            <Meta icon={Building2} label="Client" value={cs.client} />
            <Meta icon={Tag} label="Industry" value={cs.industry} />
            <Meta icon={Clock} label="Timeline" value={cs.duration} />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 relative h-[300px] sm:h-[520px] overflow-hidden rounded-2xl border border-gray-200"
        >
          <img src={cs.heroImage} alt={cs.title} className="h-full w-full object-cover" />
        </motion.div>
      </section>

      {/* Metrics */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 mt-16 sm:mt-20">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
          {cs.metrics.map((m, i) => (
            <div key={i} className="bg-white p-8 sm:p-10" data-testid={`case-metric-${i}`}>
              <div className="font-heading font-extrabold tracking-tighter text-4xl sm:text-6xl text-brand">
                {m.value}
              </div>
              <div className="mt-3 text-sm text-brand-ink/55">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 mt-20 sm:mt-28">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="col-span-12 lg:col-span-6">
            <Chapter num="01" title="The Challenge">
              <p className="text-lg leading-relaxed text-brand-ink/65">{cs.challenge}</p>
            </Chapter>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="col-span-12 lg:col-span-6">
            <Chapter num="02" title="Our Approach">
              <div className="space-y-6">
                {cs.approach.map((step, i) => (
                  <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6">
                    <h3 className="font-heading font-bold text-lg text-brand-ink">{step.title}</h3>
                    <p className="mt-2 text-brand-ink/60 leading-relaxed">{step.body}</p>
                  </div>
                ))}
              </div>
            </Chapter>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 mt-20 sm:mt-28">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {cs.gallery.map((src, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={fadeUp}
              className={`group relative overflow-hidden rounded-2xl border border-gray-200 ${
                i === 0 ? "md:col-span-2 h-[280px] sm:h-[460px]" : "h-[240px] sm:h-[340px]"
              }`}
            >
              <img
                src={src}
                alt={`${cs.title} visual ${i + 1}`}
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Results + Services */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 mt-20 sm:mt-28">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="col-span-12 lg:col-span-7">
            <Chapter num="03" title="The Results">
              <ul className="space-y-4">
                {cs.results.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-lg text-brand-ink/70">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-brand" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Chapter>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="col-span-12 lg:col-span-5 lg:pt-10">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink/40">Services delivered</div>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {cs.services.map((s, i) => (
                <span key={i} className="rounded-full border border-gray-200 bg-brand-surface px-4 py-2 text-sm font-medium text-brand-ink">
                  {s}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA + Next */}
      <section className="mt-24 sm:mt-32 bg-brand-ink">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-12 gap-10 items-end">
            <div className="col-span-12 lg:col-span-7">
              <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-5xl text-white leading-none">
                Have a project like this in mind?
              </h2>
              <p className="mt-5 text-lg text-white/55 max-w-lg">
                Let's talk about how we can bring your vision to life — immersive, learning or creative.
              </p>
              <Link
                to="/#contact"
                data-testid="case-study-cta"
                className="mt-8 group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-brand-ink hover:bg-brand hover:text-white transition-colors"
              >
                Start a project
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <button
                onClick={() => navigate(`/work/${next.slug}`)}
                data-testid="case-study-next"
                className="group w-full text-left rounded-2xl border border-white/15 p-6 hover:border-white/40 transition-colors"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">Next project</div>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <span className="font-heading font-bold text-xl text-white">{next.title}</span>
                  <ArrowUpRight className="h-6 w-6 shrink-0 text-white/50 group-hover:text-white transition-colors" />
                </div>
                <div className="mt-2 text-sm text-white/45">{next.category}</div>
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
